import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { apiService } from '../services/api';
import { useApp } from '../context/AppContext';

export const OAuthCallback: React.FC = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { loadWorkspaces, setActiveWorkspaceId } = useApp();
	const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
	const [error, setError] = useState<string | null>(null);
	const hasProcessed = useRef(false);

	useEffect(() => {
		// Prevent double execution in React StrictMode (dev mode)
		if (hasProcessed.current) {
			return;
		}
		// Mark as processed immediately to prevent race conditions
		hasProcessed.current = true;

		const handleCallback = async () => {
			const code = searchParams.get('code');
			const state = searchParams.get('state');
			const errorParam = searchParams.get('error');

			if (errorParam) {
				setError(`OAuth error: ${errorParam}`);
				setStatus('error');
				return;
			}

			if (!code || !state) {
				setError('Missing OAuth parameters');
				setStatus('error');
				return;
			}

			try {
				const response = await apiService.oauthCallback(code, state);
				if (response.success && response.data) {
					// Signal completion to parent window (if opened in popup)
					if (window.opener) {
						// Set in localStorage to trigger storage event for parent window
						localStorage.setItem('oauth_complete', JSON.stringify(response.data));
						// Also post message to opener as backup (cross-origin might block localStorage event)
						try {
							window.opener.postMessage(
								{ type: 'oauth_complete', data: response.data },
								window.location.origin
							);
						} catch (e) {
							// Cross-origin or other error, localStorage event should still work
							console.warn('Could not post message to opener:', e);
						}
						// Small delay to ensure storage event fires before closing
						setTimeout(() => {
							window.close();
						}, 100);
					} else {
						// Direct navigation - reload workspaces and optionally auto-select
						await loadWorkspaces();
						// Optionally auto-select the newly created workspace
						if (response.data.workspace?.workspace_id) {
							setActiveWorkspaceId(response.data.workspace.workspace_id);
						}
						// Redirect to main app
						setStatus('success');
						setTimeout(() => {
							navigate('/');
						}, 2000);
					}
				} else {
					setError(response.error || 'Failed to complete OAuth flow');
					setStatus('error');
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to complete OAuth flow');
				setStatus('error');
			}
		};

		handleCallback();
	}, [searchParams, navigate, loadWorkspaces, setActiveWorkspaceId]);

	if (status === 'processing') {
		return (
			<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
				<CircularProgress sx={{ mb: 2 }} />
				<Typography variant="body1">Completing OAuth authorization...</Typography>
			</Box>
		);
	}

	if (status === 'error') {
		return (
			<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" p={3}>
				<Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
					{error || 'An error occurred during OAuth authorization'}
				</Alert>
				<Typography variant="body2" color="text.secondary">
					You can close this window and try again.
				</Typography>
			</Box>
		);
	}

	return (
		<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
			<Alert severity="success" sx={{ mb: 2, maxWidth: 600 }}>
				OAuth authorization completed successfully!
			</Alert>
			<Typography variant="body2" color="text.secondary">
				Redirecting to main application...
			</Typography>
		</Box>
	);
};

